// Lógica PURA del overlay de desviación de dirección (sin React ni MapLibre),
// para poder verificarla de forma determinista bajo jest/jsdom (MapLibre usa
// WebGL, no disponible en jsdom). El componente ServiceRouteMap consume estas
// funciones para pintar el círculo + los dos geopuntos + la etiqueta.

// Colores semánticos del design system (error / éxito).
export const DEV_COLOR_EXCEEDS = "#DB2323"
export const DEV_COLOR_OK = "#34C38F"

// Construye un polígono (GeoJSON) que aproxima un círculo geodésico de `radiusM`
// metros centrado en {lat,lng}, con `steps` vértices. Conversión metros->grados:
// dLat = r/111320; dLng = r/(111320*cos(lat)). Clamp del coseno para evitar
// divisiones degeneradas cerca de los polos.
export const circlePolygon = (centerLat, centerLng, radiusM, steps = 64) => {
  const r = Math.max(Number(radiusM) || 0, 0)
  const k = Math.max(Math.cos((centerLat * Math.PI) / 180), 1e-6)
  const dLat = r / 111320
  const dLng = r / (111320 * k)
  const ring = []
  for (let i = 0; i <= steps; i += 1) {
    const theta = (i / steps) * 2 * Math.PI
    ring.push([centerLng + dLng * Math.cos(theta), centerLat + dLat * Math.sin(theta)])
  }
  return {
    type: "Feature",
    geometry: { type: "Polygon", coordinates: [ring] },
    properties: {},
  }
}

// Plan PURO del overlay: decide marcadores, círculo, línea, color y radio a
// partir de `deviationOverlay = { created, operator, deviationM, toleranceM, label }`.
// Devuelve null si no hay geopunto de creación válido (nada que pintar).
export const buildDeviationOverlayPlan = (overlay) => {
  if (!overlay) return null
  const { created, operator, deviationM, toleranceM, label } = overlay
  const cLat = Number(created?.lat)
  const cLng = Number(created?.lng)
  if (!Number.isFinite(cLat) || !Number.isFinite(cLng)) return null

  const tol = Number(toleranceM)
  const dev = deviationM == null ? null : Number(deviationM)
  const hasDev = dev != null && Number.isFinite(dev)
  const exceeds = hasDev && Number.isFinite(tol) && dev > tol
  const radiusM = hasDev ? dev : (Number.isFinite(tol) ? tol : 0)
  const color = exceeds ? DEV_COLOR_EXCEEDS : DEV_COLOR_OK

  const oLat = Number(operator?.lat)
  const oLng = Number(operator?.lng)
  const hasOperator = Number.isFinite(oLat) && Number.isFinite(oLng)

  // Dirección TEXTUAL original (creación) y del operador. El operador no tiene
  // texto (fallback a coords lo resuelve el componente); created.address es la
  // dirección con la que se levantó el servicio. Se propagan al plan para que el
  // componente pinte la etiqueta bajo cada geopunto cuando el toggle esté activo.
  const createdAddress = created?.address != null ? created.address : null
  const operatorAddress = operator?.address != null ? operator.address : null

  return {
    created: { lat: cLat, lng: cLng, address: createdAddress },
    operator: hasOperator ? { lat: oLat, lng: oLng, address: operatorAddress } : null,
    hasOperator,
    exceeds,
    radiusM,
    color,
    label: label || null,
    drawsCircle: radiusM > 0,
    circle: radiusM > 0 ? circlePolygon(cLat, cLng, radiusM, 64) : null,
  }
}

// Opciones PURAS del popup del overlay de desviación (A1). Usa una clase DEDICADA
// `svc-deviation-popup` (ADEMÁS de la compartida `svc-map-popup`) y maxWidth:"none"
// para que la etiqueta no se trunque. El CSS acotado a `.svc-deviation-popup` lo
// inyecta el componente (ensureDeviationPopupCss); NO se toca `.svc-map-popup`.
export const buildDeviationPopupOptions = () => ({
  // anchor:"bottom" ancla el popup ENCIMA del punto creciendo hacia arriba, con
  // la flecha abajo apuntando al geopunto. Evita que el contenido se recorte
  // contra el marcador y el botón de cerrar. offset vertical para separar del pin.
  anchor: "bottom",
  offset: [0, -12],
  closeButton: true,
  closeOnClick: false,
  maxWidth: "none",
  className: "svc-map-popup svc-deviation-popup",
})
